<script setup>
import {plainStripeError} from '~/utils/stripeErrors.js';
// The booking sheet (BOOKINGS_PLAN.md B5, B10, B11, BE3; AppBookSheet): how
// long (opens at the length the player chose: one slot for a click, the
// dragged range via `initialSlots`; the pills go up to the most allowed), who
// you're playing with (members by
// name, guests when the club allows them), the price from the API's quote,
// the cancel words, and Book. A paid booking charges the saved card at once;
// no card → the booking waits (PendingPayment) while a card is added; 3-D
// Secure runs in the browser and `pay/sync` settles it. Every refusal comes
// from the API in plain words.
import {clockWord} from '~/utils/bookings';
import {computed, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import MembershipSavedCardRow from '~/components/memberships/MembershipSavedCardRow.vue';
import {useAuthService} from '~/composables/useAuthService';
import {useBookingsMember, bookingApiError} from '~/composables/useBookingsMember';
import {AREA_NOUN} from '~/utils/areaWords';
import {
  adminOverridesNote,
  cutoffLine, dayWord, durationWord, lengthStop, longDate, longerReason, longestLine, maxSlotsFrom, moneyExact, openingSlots, outcomeStep, runRange,
} from '~/utils/bookingsMember';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  /** {area, slot, cell, group, column, index} from the grid. */
  pick: {type: Object, default: null},
  /** {id, name, players: [{id, userId, fullName}]} */
  club: {type: Object, default: null},
  viewer: {type: Object, default: null},
  date: {type: String, default: null},
  todayDate: {type: String, default: null},
  sportId: {type: String, default: null},
  /**
   * How many slots the sheet opens at (same name as the admin BookingDialog):
   * 1 for a click, N for a dragged range. Clamped to 1..maxSlots when the sheet
   * opens; the length pills still go up to maxSlots.
   */
  initialSlots: {type: Number, default: 1},
  /** Grant 2026-11-02: the club's own word for the area ("court", areaWords), for the line under the How long stepper. */
  noun: {type: String, default: AREA_NOUN},
  /**
   * The /features/bookings demo: {quote(body) → the quote, book(body) → the booking} stand in for the API, so the real
   * sheet runs on static data and nothing is ever booked. Null everywhere else.
   */
  demo: {type: Object, default: null},
});
const emit = defineEmits(['update:modelValue', 'booked']);

const {user} = useAuthService();
const {setAlertMessage} = useAlertService();
const api = useBookingsMember();

const open = computed({get: () => props.modelValue, set: (v) => emit('update:modelValue', v)});

// ── Allowance for this group (B4b, most generous) ──
const allowance = computed(() => props.viewer?.byGroup?.[props.pick?.group?.id] ?? props.viewer ?? {});
const slotMinutes = computed(() => props.pick?.group?.slotMinutes ?? 60);
// Grant 2026-11-02 ("Admin can do what they want with a warning"): a club admin's run is the free run itself, never
// capped by the member allowance's longest booking; the quote chips "longer than allowed" and the line under the price
// says so (the app's sheet already offers admin lengths, X77).
const isAdmin = computed(() => !!props.viewer?.isAdmin);
const maxSlots = computed(() => props.pick ? maxSlotsFrom(props.pick.column, props.pick.index, {
  slotMinutes: slotMinutes.value, maxDurationMinutes: allowance.value.maxDurationMinutes, allDay: isAdmin.value || !!allowance.value.allDay,
}) : 0);
const slots = ref(1);
// Grant 2026-11-02 ("the How long section looks messy as chips. Why not have a number of slots stepper?"): − / + one
// slot a press, from one slot to maxSlots; when + is off, the line under it says why (lengthStop / longerReason).
const limit = computed(() => props.pick ? lengthStop(props.pick.column, props.pick.index, {
  slotMinutes: slotMinutes.value, maxDurationMinutes: allowance.value.maxDurationMinutes, allDay: isAdmin.value || !!allowance.value.allDay,
}) : null);
const shorter = () => { if (slots.value > 1) slots.value -= 1; };
const longer = () => { if (slots.value < maxSlots.value) slots.value += 1; };
const lengthWord = computed(() => durationWord(slots.value * slotMinutes.value));
const lengthHelp = computed(() => {
  if (slots.value >= maxSlots.value) {
    const why = longerReason(limit.value, {noun: props.noun, maxDurationMinutes: allowance.value.maxDurationMinutes});
    if (why) return why;
  }
  return isAdmin.value ? 'Admins can book any length; a rule it goes past is noted under the price.' : longestLine(props.viewer, {groupAllowance: allowance.value});
});
const range = computed(() => props.pick ? runRange(props.pick.column, props.pick.index, slots.value, {h24: true}) : '');
const title = computed(() => props.pick ? `${props.pick.area.name} · ${dayWord(props.date, props.todayDate)}, ${clockWord(props.pick.slot.label)}` : '');

// ── Playing with (B10, BE23) ──
const participants = ref([]); // [{userId, name, isGuest}]
const memberPick = ref(null);
const guestName = ref('');
const addingGuest = ref(false);
const members = computed(() => (props.club?.players ?? [])
    .filter((p) => p.userId && p.userId !== user.value?.id && !participants.value.some((x) => x.userId === p.userId))
    .map((p) => ({userId: p.userId, name: p.fullName})));
const addMember = (m) => {
  if (!m) return;
  participants.value = [...participants.value, {userId: m.userId, name: m.name, isGuest: false}];
  memberPick.value = null;
};
const addGuest = () => {
  const name = guestName.value.trim();
  if (!name) return;
  participants.value = [...participants.value, {userId: null, name, isGuest: true}];
  guestName.value = '';
  addingGuest.value = false;
};
const remove = (i) => { participants.value = participants.value.filter((_, idx) => idx !== i); };
const guestsAllowed = computed(() => !!props.viewer?.guestsAllowed);
const guestFee = computed(() => props.viewer?.guestPriceMinor ? moneyExact(props.viewer.guestPriceMinor, props.viewer.currency) : null);

// ── Quote (price, refusal, card) ──
const quote = ref(null);
const quoting = ref(false);
const errors = ref({});
let quoteTimer = null;
const body = () => ({
  clubId: props.club?.id,
  playingAreaId: props.pick?.area.id,
  startsAt: props.pick?.slot.startsAt,
  slots: slots.value,
  sportId: props.sportId || null,
  participants: participants.value.map((p) => (p.userId ? {userId: p.userId} : {name: p.name})),
});
const refreshQuote = () => {
  if (!open.value || !props.pick) return;
  clearTimeout(quoteTimer);
  quoteTimer = setTimeout(async () => {
    quoting.value = true;
    try {
      quote.value = props.demo ? props.demo.quote(body()) : await api.quote(body());
      errors.value = {};
    } catch (e) {
      quote.value = null;
      errors.value = {global: [bookingApiError(e, "The price couldn't be checked.")]};
    } finally {
      quoting.value = false;
    }
  }, 200);
};
watch([slots, participants], refreshQuote, {deep: true});

// X54 (ruling 9): two decimal places, built from the quote's figures ("£4.00", "£4.00 + £2.00 for guests"); "Free" for nothing.
const priceLine = computed(() => {
  const q = quote.value;
  if (!q) return quoting.value ? 'Checking…' : '';
  if (!(q.totalMinor > 0)) return q.priceLine && q.totalMinor !== 0 ? q.priceLine : 'Free';
  const base = moneyExact(q.priceMinor ?? q.totalMinor, q.currency);
  return q.guestFeesMinor > 0 ? `${base} + ${moneyExact(q.guestFeesMinor, q.currency)} for guests` : base;
});
const cancelLine = computed(() => quote.value?.cancelLine || cutoffLine(props.pick?.group?.cancelCutoffMinutes ?? null));
const refusal = computed(() => quote.value && quote.value.allowed === false ? (quote.value.reason || "This can't be booked.") : null);
// Grant 2026-11-02 ("Admin can do what they want with a warning"): an admin's booking past a member rule is quoted with
// chips, not refused; one light line says what it breaks and that Book goes ahead (the app's sheet says the same).
const overrides = computed(() => (quote.value && quote.value.allowed !== false ? adminOverridesNote(quote.value.chips) : null));
const needsCard = computed(() => !!quote.value?.needsCard || (step.value === 'pay'));
const cardId = ref(null);
const hasCard = ref(false);

// ── Book (BE3 outcomes) ──
const step = ref('form'); // form | pay | wait | done
const busy = ref(false);
const booking = ref(null);
const payMessage = ref('');
const reset = () => {
  slots.value = openingSlots(props.initialSlots, maxSlots.value);
  participants.value = [];
  memberPick.value = null;
  guestName.value = '';
  addingGuest.value = false;
  quote.value = null;
  errors.value = {};
  step.value = 'form';
  booking.value = null;
  payMessage.value = '';
  refreshQuote();
};
watch(() => [props.modelValue, props.pick], ([isOpen]) => { if (isOpen) reset(); }, {immediate: true});

const finish = async (res) => {
  booking.value = res;
  const next = outcomeStep(res);
  if (next === 'act' && res.clientSecret) {
    const {error} = await api.finishCardAction({clientSecret: res.clientSecret, stripeAccountId: res.stripeAccountId});
    if (error) { step.value = 'pay'; errors.value = {global: [plainStripeError(error, "Your card didn't go through.")]}; return; }
    return finish(await api.paySync(res.id));
  }
  if (next === 'done') {
    setAlertMessage(res.message || `Booked: ${res.line}`);
    emit('booked', res);
    open.value = false;
    return;
  }
  if (next === 'pay') { step.value = 'pay'; payMessage.value = res.message || 'Add a card to pay for this booking. The slot is held for 10 minutes.'; return; }
  if (next === 'failed') { step.value = 'pay'; errors.value = {global: [res.message || "Your card didn't go through. Try another card."]}; return; }
  // pendingPayment: the webhook confirms it; ask once more in a moment.
  step.value = 'wait';
  payMessage.value = res.message || "We're confirming your payment…";
  setTimeout(async () => {
    try { await finish(await api.paySync(res.id)); } catch { /* the reminder email covers it */ }
  }, 2500);
};
const book = async () => {
  busy.value = true;
  errors.value = {};
  try {
    await finish(props.demo ? props.demo.book(body()) : await api.book({...body(), userPaymentMethodId: needsCard.value ? cardId.value : null}));
  } catch (e) {
    errors.value = {global: [bookingApiError(e, "That couldn't be booked.")]};
  } finally {
    busy.value = false;
  }
};
const payNow = async () => {
  if (!booking.value) return;
  busy.value = true;
  errors.value = {};
  try {
    await finish(await api.pay(booking.value.id, cardId.value));
  } catch (e) {
    errors.value = {global: [bookingApiError(e, "The payment couldn't be taken.")]};
  } finally {
    busy.value = false;
  }
};
const canBook = computed(() => !!quote.value && quote.value.allowed !== false && !quoting.value && (!quote.value.needsCard || hasCard.value));
const bookLabel = computed(() => {
  const base = `Book ${props.pick?.area.name ?? ''}, ${range.value}`;
  return quote.value?.totalMinor > 0 ? `${base} · ${moneyExact(quote.value.totalMinor, quote.value.currency)}` : base;
});
</script>

<template>
  <tl-dialog v-model="open" :title="title" :max-width="440">
    <template v-if="pick">
      <div class="bs-date">{{ longDate(date) }}</div>

      <!-- Done waiting on a card / 3-D Secure -->
      <template v-if="step === 'pay' || step === 'wait'">
        <div class="bs-rows mt-3">
          <div class="bs-row"><span class="k">Booking</span><span class="v">{{ booking?.line || (pick.area.name + ' · ' + range) }}</span></div>
          <div class="bs-row"><span class="k">To pay</span><span class="v bs-amt">{{ moneyExact(booking?.totalMinor ?? quote?.totalMinor ?? 0, booking?.currency ?? quote?.currency) }}</span></div>
        </div>
        <p class="bs-note">{{ payMessage }}</p>
        <template v-if="step === 'pay'">
          <div class="tl-label mb-1 mt-2">Your card</div>
          <membership-saved-card-row @change="cardId = $event" @loaded="hasCard = $event.hasCard"/>
        </template>
        <div v-else class="bs-loading"><v-progress-circular indeterminate size="20" width="2" color="primary"/> One moment…</div>
        <crud-error-display class="mt-3" :errors="errors"/>
      </template>

      <template v-else>
        <!-- How long (B5; Grant 2026-11-02: a slots stepper, one slot a press, never chips) -->
        <div class="tl-label mt-3 mb-1">How long</div>
        <div class="bs-step" data-test="booking-length">
          <tl-icon-btn tooltip="Shorter" icon="mdi-minus" :icon-size="20" :disabled="slots <= 1" data-test="booking-length-shorter" @click="shorter"/>
          <div class="bs-step-value" data-test="booking-length-value">{{ lengthWord }} · {{ range }}</div>
          <tl-icon-btn tooltip="Longer" icon="mdi-plus" :icon-size="20" :disabled="slots >= maxSlots" data-test="booking-length-longer" @click="longer"/>
        </div>
        <div class="bs-help" data-test="booking-length-help">{{ lengthHelp }}</div>

        <!-- Playing with (B10) -->
        <div class="tl-label mt-4 mb-1">Playing with <span class="bs-opt">(optional)</span></div>
        <div v-if="participants.length" class="bs-people">
          <span v-for="(p, i) in participants" :key="i" class="tl-chip" :class="{'tl-chip--brand': !p.isGuest}">
            {{ p.name }}<template v-if="p.isGuest"> · guest</template>
            <button type="button" class="bs-chip-x" :aria-label="'Remove ' + p.name" @click="remove(i)"><v-icon size="13" icon="mdi-close"/></button>
          </span>
        </div>
        <div class="bs-add">
          <v-autocomplete v-if="members.length" v-model="memberPick" :items="members" item-title="name" item-value="userId" return-object
                          variant="outlined" density="compact" hide-details placeholder="Add a player" prepend-inner-icon="mdi-account-plus-outline"
                          class="bs-add-field" :menu-props="{maxHeight: 240}" @update:model-value="addMember"/>
          <template v-if="guestsAllowed">
            <v-btn v-if="!addingGuest" variant="tonal" color="primary" rounded="pill" size="small" prepend-icon="mdi-account-question-outline" @click="addingGuest = true">
              Add a guest<template v-if="guestFee"> · {{ guestFee }}</template>
            </v-btn>
            <div v-else class="bs-guest">
              <v-text-field v-model="guestName" variant="outlined" density="compact" hide-details placeholder="Guest's name" autofocus @keydown.enter.prevent="addGuest"/>
              <v-btn color="primary" rounded="pill" size="small" :disabled="!guestName.trim()" @click="addGuest">Add</v-btn>
            </div>
          </template>
        </div>

        <!-- Price and the cancel words (B11) -->
        <div class="bs-rows mt-4">
          <div class="bs-row"><span class="k">Price</span><span class="v bs-amt">{{ priceLine }}</span></div>
          <div v-if="cancelLine" class="bs-row"><span class="k">Cancelling</span><span class="v">{{ cancelLine }}</span></div>
        </div>
        <v-alert v-if="refusal" type="warning" variant="tonal" density="compact" class="mt-3">{{ refusal }}</v-alert>
        <v-alert v-else-if="overrides" type="info" variant="tonal" density="compact" icon="mdi-shield-account-outline" class="mt-3">{{ overrides }}</v-alert>

        <!-- X54 (D2 L15): no card step on a refused quote (it used to show a saved card under "can't take payments"). -->
        <template v-if="quote?.needsCard && !refusal">
          <div class="tl-label mt-4 mb-1">Your card</div>
          <membership-saved-card-row note="Charged when you book. Refunded in full if you cancel before the cut-off." @change="cardId = $event" @loaded="hasCard = $event.hasCard"/>
        </template>
        <crud-error-display class="mt-3" :errors="errors"/>
      </template>
    </template>

    <template #actions>
      <v-btn variant="outlined" :disabled="busy" @click="open = false">{{ step === 'pay' ? 'Pay later' : 'Cancel' }}</v-btn>
      <v-btn v-if="step === 'pay'" color="primary" :loading="busy" :disabled="!hasCard" prepend-icon="mdi-lock" @click="payNow">Pay {{ moneyExact(booking?.totalMinor ?? 0, booking?.currency) }}</v-btn>
      <v-btn v-else-if="step === 'form'" color="primary" :loading="busy" :disabled="!canBook" @click="book">{{ bookLabel }}</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.bs-date { font-size: 13px; color: rgb(var(--v-theme-textSubtle)); }
/* Grant 2026-11-02: the How long stepper, [−] length · range [+]; the kit icon buttons grown to a 44px target. */
.bs-step {
  display: flex; align-items: center; gap: 4px; padding: 4px;
  border: 1px solid rgb(var(--v-theme-borderLight)); border-radius: 12px; background: rgb(var(--v-theme-surface));
  :deep(.tl-iconbtn) { width: 44px; height: 44px; flex-shrink: 0; }
}
.bs-step-value {
  flex: 1; min-width: 0; text-align: center; font-size: 14px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
.bs-help { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 6px; }
.bs-opt { font-weight: 500; text-transform: none; letter-spacing: 0; color: rgb(var(--v-theme-textSubtle)); }
.bs-people { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.bs-chip-x { border: 0; background: none; padding: 0 0 0 2px; display: inline-flex; cursor: pointer; color: inherit; }
.bs-add { display: flex; flex-direction: column; gap: 8px; }
.bs-add-field { max-width: 100%; }
.bs-guest { display: flex; gap: 8px; align-items: center; }
.bs-rows { border: 1px solid rgb(var(--v-theme-borderLight)); border-radius: 12px; overflow: hidden; background: rgb(var(--v-theme-light)); }
.bs-row {
  display: flex; gap: 12px; align-items: center; padding: 9px 14px; font-size: 13px; border-top: 1px solid rgb(var(--v-theme-borderLight));
  &:first-child { border-top: 0; }
  .k { width: 90px; flex-shrink: 0; font-size: 12px; font-weight: 600; color: rgb(var(--v-theme-textSubtle)); }
  .v { flex: 1; line-height: 1.45; }
}
.bs-amt { font-weight: 800; font-size: 15px; }
.bs-note { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.5; margin: 12px 2px 0; }
.bs-loading { display: flex; align-items: center; gap: 10px; font-size: 13px; color: rgb(var(--v-theme-textSubtle)); padding: 12px 0; }
</style>
